import React, { useEffect, useState } from "react";
import { Table, Descriptions, Button, Input, message } from "antd";
import { Content } from "antd/es/layout/layout";
import { FaSave } from "react-icons/fa";
import { request } from "../../functions/request";
import "./correcao.css";

function formatDate(dateValue) {
  return new Date(dateValue).toLocaleDateString("pt-BR", {
    timeZone: "America/Sao_Paulo",
  });
}

// Mesma lógica de busca do CorrecaoSearch.js (inclusive a expansão de
// "TODOS" em uma request por UF), só devolvendo as linhas cruas pra dar
// pra juntar com as outras cotações.
async function fetchLinhasCotacao(query, allUfs) {
  const params = new URLSearchParams(query);
  const data_start = params.get("data_start");
  const data_final = params.get("data_final");
  const ordem = params.get("ordem");
  const uf = params.get("uf") ?? "";

  let path = `/correcoes/getcorrecoes`;
  if (data_start && data_final) {
    path += `?data_start=${data_start}&data_final=${data_final}`;
  }
  if (ordem) {
    path += `&ordem=${ordem}`;
  }

  if (uf.toUpperCase() === "TODOS") {
    const ufsPromises = (allUfs || []).map(async (ufItem) => {
      const response = await request(`${path}&uf=${ufItem.uf}`, {
        method: "GET",
      });
      return response[0] || [];
    });
    const resultados = await Promise.all(ufsPromises);
    return resultados.flat();
  }

  const finalPath = uf ? `${path}&uf=${uf}` : path;
  const response = await request(finalPath, { method: "GET" });
  return response[0] || [];
}

/**
 * Versão editável do CorrecaoRelatorioMesclado: junta a correção de várias
 * cotações numa única tabela (uma linha por UF+Data), com um campo de
 * input editável por cotação selecionada em vez de uma tabela separada
 * por cotação.
 */
export default function CorrecaoSearchMesclado({ queries, allUfs }) {
  const [dadosTabela, setDadosTabela] = useState([]);
  const [cotacoesInfo, setCotacoesInfo] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let cancelado = false;

    async function montar() {
      if (!queries || queries.length === 0) return;
      setIsLoading(true);
      try {
        const porCotacao = await Promise.all(
          queries.map(async ({ id, query, nome }) => {
            const linhas = await fetchLinhasCotacao(query, allUfs);
            const ordem = new URLSearchParams(query).get("ordem");
            return { id, nome, ordem, linhas };
          })
        );

        const linhasPorChave = new Map();
        porCotacao.forEach(({ id, linhas }) => {
          linhas.forEach((linha) => {
            const chave = `${linha.campouf}|${linha.campodt}`;
            if (!linhasPorChave.has(chave)) {
              linhasPorChave.set(chave, {
                key: chave,
                campouf: linha.campouf,
                campodtOriginal: linha.campodt,
              });
            }
            linhasPorChave.get(chave)[`valor_${id}`] = linha.campo;
          });
        });

        const linhasFinais = Array.from(linhasPorChave.values())
          .sort((a, b) => {
            if (a.campouf !== b.campouf) {
              return a.campouf.localeCompare(b.campouf);
            }
            return new Date(b.campodtOriginal) - new Date(a.campodtOriginal);
          })
          .map((registro) => ({
            ...registro,
            campodt: formatDate(registro.campodtOriginal),
          }));

        if (!cancelado) {
          setDadosTabela(linhasFinais);
          setCotacoesInfo(porCotacao.map(({ id, nome, ordem }) => ({ id, nome, ordem })));
        }
      } catch (error) {
        console.error("Erro ao mesclar cotações pra edição:", error);
        message.error("Ocorreu um erro inesperado ao carregar as cotações!");
      } finally {
        if (!cancelado) setIsLoading(false);
      }
    }

    montar();
    return () => {
      cancelado = true;
    };
  }, [queries, allUfs]);

  const alterarValor = (chaveLinha, cotId, novoValor) => {
    setDadosTabela((prev) =>
      prev.map((linha) =>
        linha.key === chaveLinha
          ? { ...linha, [`valor_${cotId}`]: novoValor }
          : linha
      )
    );
  };

  const salvar = async () => {
    setIsSaving(true);
    let houveErro = false;
    for (const linha of dadosTabela) {
      for (const { id, ordem } of cotacoesInfo) {
        const valor = linha[`valor_${id}`];
        if (valor === undefined || valor === null || valor === "") continue;

        let query = `salvarCorrecoes?ordem=${encodeURIComponent(ordem)}`;
        if (linha.campouf) {
          query += `&uf=${encodeURIComponent(linha.campouf)}`;
        }

        try {
          await request(`/correcoes/${query}`, {
            method: "POST",
            body: JSON.stringify({
              campouf: linha.campouf,
              campodt: linha.campodtOriginal,
              campo: String(valor).replace(/,/g, "."),
            }),
          });
        } catch (error) {
          houveErro = true;
          console.error("Erro ao salvar correção mesclada:", error);
        }
      }
    }
    setIsSaving(false);
    if (houveErro) {
      message.error("Algumas cotações não foram salvas. Confira e tente novamente.");
    } else {
      message.success("Dados atualizados com sucesso!");
    }
  };

  const columnsTable = [
    { title: "UF", dataIndex: "campouf", fixed: "left", width: 90 },
    { title: "Data", dataIndex: "campodt", fixed: "left", width: 110 },
    ...cotacoesInfo.map(({ id, nome }) => ({
      title: nome,
      dataIndex: `valor_${id}`,
      render: (valor, registro) => (
        <Input
          className="input-number"
          value={valor}
          onChange={(event) =>
            alterarValor(registro.key, id, event.target.value)
          }
        />
      ),
    })),
  ];

  return (
    <Content
      style={{
        padding: 10,
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        zIndex: 0,
      }}
      className="slide-right"
    >
      <Descriptions
        title={"Correção Mesclada"}
        column={1}
        items={[
          {
            key: "1",
            label: "Cotações",
            children: (queries || []).map((q) => q.nome).join(", "),
          },
        ]}
        extra={
          <Button
            icon={<FaSave />}
            type="primary"
            loading={isSaving}
            onClick={salvar}
          >
            Atualizar
          </Button>
        }
      />
      <Table
        loading={isLoading}
        columns={columnsTable}
        dataSource={dadosTabela}
        pagination={false}
        scroll={{ x: "max-content" }}
      />
    </Content>
  );
}
